import React, { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Box, Tooltip } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Grid from '@mui/material/Grid'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import MenuIcon from '@mui/icons-material/Menu'
import Home from '@mui/icons-material/Home'
import Avatar from '@mui/material/Avatar'
import { useTheme } from '@mui/material/styles'
import { useIsMobileLayout } from '../../utils/hooks/useIsMobileLayout'
import MenuWithIcon, { MenuActionItem } from './MenuWithIcon'
import ThemeModeMenuSection from './ThemeModeMenuSection'
import LayoutHeaderPrimaryNav from './LayoutHeaderPrimaryNav'
import LayoutSidebarShell from './LayoutSidebarShell'
import { headerHeight, mobileHeaderActionsRowHeight, mobileHeaderLogoRowHeight } from './tabNavStyles'
import { headerLogoBoxBackground } from './headerColorVariants'
import { useHeaderColorScheme } from '../../utils/providers/headerColorContext'
import { useCurrentUser } from '../../utils/hooks/useUserContext'
import { useLogo } from '../../utils/providers/themeContext'
import { logout } from '../../utils/tokenManagement'
import { apolloClient } from '../../apollo'
import { useAppTranslations } from '../../utils/hooks/useAppTranslations'
import { CompanyRouteBuilder } from '../../pages/routeParams'
import { useSidebarContext } from '../../utils/providers/sidebarContext'
import { GlobalFormActionsHost, GlobalFormActionsProvider } from '../form/GlobalFormActions'
import ImpersonationWarningBanner from './ImpersonationWarningBanner'
import powerspaceLogo from '../../img/logo_powerspace.svg'
import powerspaceHeaderLogo from '../../img/logo_powerspace_big.svg'
import SeasonalLogo from './SeasonalLogo'
import { getActiveSeasonalPeriodForUser, SeasonalPeriod } from './seasonalPeriods'
import { useUserPermissions } from '../../pages/company/userPermissions'

export const getSeasonalPeriodForLogo = (
  logo: string,
  costHistory: boolean,
  date: Date = new Date()
): SeasonalPeriod | null => (logo === powerspaceLogo ? getActiveSeasonalPeriodForUser(costHistory, date) : null)

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate()
  const t = useAppTranslations()
  const data = useCurrentUser()
  const logo = useLogo()
  const theme = useTheme()
  const { scheme } = useHeaderColorScheme()
  const isSmallScreen = useIsMobileLayout()
  const headerIconColor = scheme.isLight ? 'default' : 'inherit'
  const { companySlug, toggle } = useSidebarContext()
  const hasSidebar = companySlug !== null
  const { costHistory } = useUserPermissions()
  const seasonalPeriod = isSmallScreen ? null : getSeasonalPeriodForLogo(logo, costHistory)

  const companyUrl = CompanyRouteBuilder(data.me.company.slug, data.me.company.type)
  const userItems = [
    {
      link: `${companyUrl}/users/${data.me.id}`,
      text: 'layout.profile',
    },
    {
      link: companyUrl,
      text: 'layout.company',
    },
  ]

  const logoutAction: MenuActionItem[] = [
    {
      text: t('layout.logout'),
      action: () => {
        logout(apolloClient)
        navigate('/login')
      },
      autoClose: true,
    },
  ]

  const [avatarAlt, avatarContent] = useMemo(() => {
    if (!data?.me) return ['', '']
    return [
      `${data.me.firstName} ${data.me.lastName}`,
      `${(data.me?.firstName ?? '').split(' ')[0][0]}${(data.me?.lastName ?? '').split(' ')[0][0]}`,
    ]
  }, [data?.me])

  const userMenuIconButtonSx = useMemo(
    () => ({
      borderRadius: theme.shape.borderRadius,
      ...(isSmallScreen ? { color: theme.palette.common.white } : {}),
      '&:hover': {
        backgroundColor: isSmallScreen ? 'rgba(255, 255, 255, 0.08)' : scheme.tabHoverBackground,
      },
    }),
    [isSmallScreen, scheme.tabHoverBackground, theme]
  )

  const userMenu = (
    <MenuWithIcon
      linkItems={userItems}
      menuContent={(closeMenu) => <ThemeModeMenuSection onSelect={closeMenu} />}
      actionItems={logoutAction}
      dataId="userMenuIcon"
      iconButtonColor={isSmallScreen ? 'inherit' : headerIconColor}
      iconButtonSx={userMenuIconButtonSx}
    >
      <Avatar alt={avatarAlt} sx={{ width: 32, height: 32, fontSize: '1.4rem' }}>
        {avatarContent}
      </Avatar>
      <KeyboardArrowDownIcon />
    </MenuWithIcon>
  )

  const homeButton = (
    <Tooltip title="Home">
      <IconButton color={headerIconColor} component={Link} to="/" size="large">
        <Home data-testid="homeIcon" />
      </IconButton>
    </Tooltip>
  )

  const desktopHeaderActions = (
    <>
      <GlobalFormActionsHost />
      {homeButton}
      {userMenu}
    </>
  )

  const sidebarToggle = hasSidebar ? (
    <Tooltip title={t('layout.sidebar.open')}>
      <IconButton
        data-testid="sidebarMobileToggle"
        onClick={toggle}
        size="large"
        sx={{ color: theme.palette.common.white }}
      >
        <MenuIcon />
      </IconButton>
    </Tooltip>
  ) : null

  const isPowerspaceLogo = logo === powerspaceLogo
  const useBigHeaderLogo = isPowerspaceLogo && !isSmallScreen
  const headerLogo = useBigHeaderLogo ? powerspaceHeaderLogo : logo
  const logoLinkSx = {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    lineHeight: 0,
    '& > img': {
      display: 'block',
      height: useBigHeaderLogo ? headerHeight : '2.8rem',
    },
  } as const

  const logoLink = (
    <Box component={Link} to="/home" sx={logoLinkSx}>
      {seasonalPeriod ? (
        <SeasonalLogo
          key={seasonalPeriod}
          src={headerLogo}
          alt="Powerspace"
          period={seasonalPeriod}
          imageHeight={headerHeight}
        />
      ) : (
        <img src={headerLogo} alt="Powerspace" />
      )}
    </Box>
  )

  return (
    <GlobalFormActionsProvider>
      <Grid
        container
        sx={{
          height: '100%',
          flexGrow: 1,
          flexDirection: 'column',
          display: 'flex',
          overflow: 'hidden',
        }}
      >
        <Grid sx={{ flex: 0, maxWidth: '100%' }}>
          <ImpersonationWarningBanner />
          <AppBar
            position="static"
            sx={{
              backgroundColor: scheme.appBarBackground,
              color: scheme.isLight ? theme.palette.text.primary : theme.palette.common.white,
            }}
          >
            <Toolbar
              disableGutters
              sx={{
                alignItems: 'stretch',
                flexDirection: isSmallScreen ? 'column' : 'row',
                height: isSmallScreen ? 'auto' : headerHeight,
                minHeight: isSmallScreen ? 'auto' : headerHeight,
                py: 0,
                [theme.breakpoints.up('sm')]: {
                  height: isSmallScreen ? 'auto' : headerHeight,
                  minHeight: isSmallScreen ? 'auto' : headerHeight,
                },
              }}
            >
              {isSmallScreen ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <Box
                    sx={{
                      alignItems: 'center',
                      backgroundColor: headerLogoBoxBackground,
                      display: 'flex',
                      height: mobileHeaderLogoRowHeight,
                      minWidth: 0,
                      position: 'relative',
                      px: 1,
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{ alignItems: 'center', display: 'flex', flex: 1, justifyContent: 'flex-start', minWidth: 0 }}
                    >
                      {sidebarToggle}
                    </Box>
                    <Box
                      sx={{
                        left: '50%',
                        position: 'absolute',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                      }}
                    >
                      {logoLink}
                    </Box>
                    <Box
                      sx={{ alignItems: 'center', display: 'flex', flex: 1, justifyContent: 'flex-end', minWidth: 0 }}
                    >
                      <GlobalFormActionsHost />
                      {userMenu}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      alignItems: 'stretch',
                      display: 'flex',
                      height: mobileHeaderActionsRowHeight,
                      minWidth: 0,
                      position: 'relative',
                      width: '100%',
                      ...(scheme.appBarBorderBottom && {
                        '&::after': {
                          backgroundColor: scheme.appBarBorderBottom,
                          bottom: 0,
                          content: '""',
                          height: '1px',
                          left: 0,
                          pointerEvents: 'none',
                          position: 'absolute',
                          right: 0,
                          zIndex: 0,
                        },
                      }),
                    }}
                  >
                    <LayoutHeaderPrimaryNav />
                  </Box>
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      alignItems: 'center',
                      backgroundColor: headerLogoBoxBackground,
                      display: 'flex',
                      flexShrink: 0,
                      height: headerHeight,
                      justifyContent: 'center',
                      width: theme.layout.sidebarWidth,
                    }}
                  >
                    {logoLink}
                  </Box>
                  <Box
                    sx={{
                      alignItems: 'stretch',
                      display: 'flex',
                      flex: 1,
                      height: headerHeight,
                      minWidth: 0,
                      position: 'relative',
                      ...(scheme.appBarBorderBottom && {
                        '&::after': {
                          backgroundColor: scheme.appBarBorderBottom,
                          bottom: 0,
                          content: '""',
                          height: '1px',
                          left: 0,
                          pointerEvents: 'none',
                          position: 'absolute',
                          right: 0,
                          zIndex: 0,
                        },
                      }),
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flex: 1,
                        height: '100%',
                        minWidth: 0,
                        overflow: 'visible',
                      }}
                    >
                      <LayoutHeaderPrimaryNav />
                    </Box>
                    <Box
                      sx={{
                        alignSelf: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        flexShrink: 0,
                        pr: 1,
                      }}
                    >
                      {desktopHeaderActions}
                    </Box>
                  </Box>
                </>
              )}
            </Toolbar>
          </AppBar>
        </Grid>
        <Grid
          sx={{
            flex: 1,
            overflow: 'hidden',
            maxWidth: '100%',
            backgroundColor: theme.palette.background.default,
          }}
        >
          <LayoutSidebarShell>{children}</LayoutSidebarShell>
        </Grid>
      </Grid>
    </GlobalFormActionsProvider>
  )
}

export default Layout
